import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { formatApiError } from "@/lib/inmindApi";
import Logo from "@/components/Logo";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleSignIn() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      const next = location.state?.from?.pathname || (u.role === "admin" ? "/admin" : "/");
      navigate(next, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12" style={{ backgroundColor: "var(--inmind-bg)" }}>
      <div className="max-w-sm w-full" data-testid="login-page">
        <div className="flex items-center gap-3 mb-8">
          <Logo size={44} />
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--inmind-muted)]">
              Welcome back
            </div>
            <h1 className="font-serif text-3xl text-[var(--inmind-text)] leading-none mt-1">InThink</h1>
          </div>
        </div>

        <Card
          className="rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.35)]"
          style={{ background: "var(--inmind-card)", border: "1px solid var(--inmind-border)" }}
        >
          <CardContent className="p-7">
            <h2 className="font-serif text-2xl mb-1" style={{ color: "var(--inmind-text)" }}>Sign in</h2>
            <p className="text-sm mb-6" style={{ color: "var(--inmind-text-secondary)" }}>Pick up where you left off.</p>

            <Button
              type="button"
              onClick={startGoogleSignIn}
              data-testid="google-login"
              className="w-full h-11 rounded-xl transition-colors gap-2"
              style={{
                background: "var(--inmind-card-subtle)",
                color: "var(--inmind-text)",
                border: "1px solid var(--inmind-border)",
              }}
            >
              <GoogleIcon />
              Continue with Google
            </Button>

            <div className="flex items-center gap-2 my-5">
              <div className="flex-1 h-px" style={{ background: "var(--inmind-border)" }} />
              <span className="text-[10px] tracking-[0.22em] uppercase font-semibold" style={{ color: "var(--inmind-muted)" }}>or email</span>
              <div className="flex-1 h-px" style={{ background: "var(--inmind-border)" }} />
            </div>

            <form onSubmit={submit} className="space-y-3">
              <Input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                data-testid="login-email"
                className="h-11 rounded-xl"
                style={{
                  background: "var(--inmind-card-subtle)",
                  color: "var(--inmind-text)",
                  border: "1px solid var(--inmind-border)",
                }}
              />
              <Input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password"
                className="h-11 rounded-xl"
                style={{
                  background: "var(--inmind-card-subtle)",
                  color: "var(--inmind-text)",
                  border: "1px solid var(--inmind-border)",
                }}
              />
              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit"
                className="w-full h-11 rounded-xl transition-colors"
                style={{ background: "var(--inmind-primary)", color: "#12122A" }}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
              </Button>
            </form>

            <div className="flex items-center justify-between text-sm mt-4">
              <button
                type="button"
                onClick={() => setShowForgot(true)}
                data-testid="forgot-password-link"
                className="hover:underline"
                style={{ color: "var(--inmind-text-secondary)" }}
              >
                Forgot password?
              </button>
              <Link to="/register" className="font-medium hover:underline" style={{ color: "var(--inmind-primary)" }} data-testid="link-to-register">
                Create account
              </Link>
            </div>
          </CardContent>
        </Card>

        <Dialog open={showForgot} onOpenChange={setShowForgot}>
          <DialogContent data-testid="forgot-dialog" className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-serif">Forgot your password?</DialogTitle>
              <DialogDescription className="text-[var(--inmind-text-secondary)] leading-relaxed">
                If your email is a Google account, the quickest fix is to{" "}
                <button onClick={() => { setShowForgot(false); startGoogleSignIn(); }} className="text-[var(--inmind-primary)] underline">
                  sign in with Google
                </button>
                {" "}— same account, no password needed.
              </DialogDescription>
            </DialogHeader>
            <p className="text-sm text-[var(--inmind-text-secondary)] mt-2 leading-relaxed">
              Otherwise, please email <span className="font-medium text-[var(--inmind-text)]">support</span> from the address you registered with and we'll help you reset.
            </p>
            <Button
              onClick={() => setShowForgot(false)}
              data-testid="forgot-close"
              className="mt-2 rounded-xl bg-[var(--inmind-primary)] text-white hover:bg-[var(--inmind-primary-hover)]"
            >
              Got it
            </Button>
          </DialogContent>
        </Dialog>
      </div>

      {/* Footer with legal links — required for store listings */}
      <div className="mt-8 text-[11px] text-[var(--inmind-muted)] flex items-center justify-center gap-3">
        <a href="/privacy" className="hover:text-[var(--inmind-text-secondary)] transition-colors" data-testid="login-privacy-link">Privacy</a>
        <span className="opacity-40">·</span>
        <a href="/support" className="hover:text-[var(--inmind-text-secondary)] transition-colors" data-testid="login-support-link">Support</a>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303C33.972 32.91 29.404 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.651-.389-3.917z" />
      <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.197l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.382 0-9.943-3.066-11.281-7.957l-6.519 5.025C9.51 39.556 16.227 44 24 44z" />
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.97 39.205 44 34 44 24c0-1.341-.138-2.651-.389-3.917z" />
    </svg>
  );
}
